import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Switch,
  Alert,
  CircularProgress,
} from '@mui/material'

const CADENCE_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'periodic', label: 'Periodic' },
]

/**
 * Calculate a sensible default min_spacing_days from cadence parameters.
 * For weekly: floor(7 / times_per_week)
 * For periodic: floor(period_days / times_per_period)
 */
function defaultSpacing(cadence, { times_per_week = 3, times_per_period = 2, period_days = 30 } = {}) {
  if (cadence === 'daily') return 0
  if (cadence === 'weekly') return Math.floor(7 / times_per_week)
  if (cadence === 'periodic') return Math.floor(period_days / times_per_period)
  return 1
}

function ScheduleConfigDialog({ open, onClose, pathway, onSave, saving }) {
  const [config, setConfig] = useState({
    cadence: 'weekly',
    is_mandatory: false,
    times_per_week: 3,
    times_per_period: 2,
    period_days: 30,
    min_spacing_days: 2,
  })

  useEffect(() => {
    if (pathway) {
      setConfig({
        cadence: pathway.cadence || 'weekly',
        is_mandatory: pathway.is_mandatory || false,
        times_per_week: pathway.times_per_week ?? 3,
        times_per_period: pathway.times_per_period ?? 2,
        period_days: pathway.period_days ?? 30,
        min_spacing_days: pathway.min_spacing_days ?? defaultSpacing(
          pathway.cadence || 'weekly',
          { times_per_week: pathway.times_per_week ?? 3, times_per_period: pathway.times_per_period ?? 2, period_days: pathway.period_days ?? 30 }
        ),
      })
    }
  }, [pathway])

  const handleCadenceChange = (_, newCadence) => {
    if (!newCadence) return
    const spacing = defaultSpacing(newCadence, config)
    setConfig((prev) => ({ ...prev, cadence: newCadence, min_spacing_days: spacing }))
  }

  const handleSave = () => {
    onSave({
      track_id: pathway.id,
      position: pathway.position,
      base_priority: pathway.base_priority,
      cadence: config.cadence,
      is_mandatory: config.is_mandatory,
      times_per_week: config.cadence === 'weekly' ? config.times_per_week : null,
      times_per_period: config.cadence === 'periodic' ? config.times_per_period : null,
      period_days: config.cadence === 'periodic' ? config.period_days : null,
      min_spacing_days: config.min_spacing_days,
    })
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Schedule Configuration
        {pathway && (
          <Typography variant="body2" color="text.secondary">
            {pathway.name}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        <Box display="grid" gridTemplateColumns="120px 1fr" gap={1.5} alignItems="center" sx={{ mt: 1 }}>
          {/* Cadence */}
          <Typography variant="body2" color="text.secondary">Cadence</Typography>
          <Box>
            <ToggleButtonGroup
              value={config.cadence}
              exclusive
              onChange={handleCadenceChange}
              size="small"
            >
              {CADENCE_OPTIONS.map((opt) => (
                <ToggleButton key={opt.value} value={opt.value}>
                  {opt.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {config.cadence === 'daily' && 'Pathway appears every session'}
              {config.cadence === 'weekly' && 'Pathway appears N times per week'}
              {config.cadence === 'periodic' && 'Pathway appears N times per custom period'}
            </Typography>
          </Box>

          {/* Weekly fields */}
          {config.cadence === 'weekly' && (
            <>
              <Typography variant="body2" color="text.secondary">Per week</Typography>
              <TextField
                size="small"
                type="number"
                value={config.times_per_week}
                onChange={(e) => {
                  const val = Math.max(1, Math.min(7, parseInt(e.target.value) || 1))
                  setConfig((prev) => ({
                    ...prev,
                    times_per_week: val,
                    min_spacing_days: defaultSpacing('weekly', { times_per_week: val }),
                  }))
                }}
                inputProps={{ min: 1, max: 7 }}
                sx={{ width: 150 }}
              />

              <Typography variant="body2" color="text.secondary">Min spacing</Typography>
              <TextField
                size="small"
                type="number"
                value={config.min_spacing_days}
                onChange={(e) =>
                  setConfig((prev) => ({
                    ...prev,
                    min_spacing_days: Math.max(0, Math.min(6, parseInt(e.target.value) || 0)),
                  }))
                }
                inputProps={{ min: 0, max: 6 }}
                sx={{ width: 150 }}
                helperText="Days between sessions"
              />
            </>
          )}

          {/* Periodic fields */}
          {config.cadence === 'periodic' && (
            <>
              <Typography variant="body2" color="text.secondary">Per period</Typography>
              <TextField
                size="small"
                type="number"
                value={config.times_per_period}
                onChange={(e) => {
                  const val = Math.max(1, Math.min(10, parseInt(e.target.value) || 1))
                  setConfig((prev) => ({
                    ...prev,
                    times_per_period: val,
                    min_spacing_days: defaultSpacing('periodic', { times_per_period: val, period_days: prev.period_days }),
                  }))
                }}
                inputProps={{ min: 1, max: 10 }}
                sx={{ width: 150 }}
              />

              <Typography variant="body2" color="text.secondary">Period days</Typography>
              <TextField
                size="small"
                type="number"
                value={config.period_days}
                onChange={(e) => {
                  const val = Math.max(7, Math.min(365, parseInt(e.target.value) || 7))
                  setConfig((prev) => ({
                    ...prev,
                    period_days: val,
                    min_spacing_days: defaultSpacing('periodic', { times_per_period: prev.times_per_period, period_days: val }),
                  }))
                }}
                inputProps={{ min: 7, max: 365 }}
                sx={{ width: 150 }}
              />

              <Typography variant="body2" color="text.secondary">Min spacing</Typography>
              <TextField
                size="small"
                type="number"
                value={config.min_spacing_days}
                onChange={(e) =>
                  setConfig((prev) => ({
                    ...prev,
                    min_spacing_days: Math.max(0, Math.min(30, parseInt(e.target.value) || 0)),
                  }))
                }
                inputProps={{ min: 0, max: 30 }}
                sx={{ width: 150 }}
                helperText="Days between sessions"
              />
            </>
          )}

          {/* Daily info */}
          {config.cadence === 'daily' && (
            <>
              <Box />
              <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
                Daily pathways appear every session. Spacing is locked to 0.
              </Alert>
            </>
          )}

          {/* Required toggle */}
          <Typography variant="body2" color="text.secondary">Required</Typography>
          <Box>
            <Switch
              checked={config.is_mandatory}
              onChange={(e) =>
                setConfig((prev) => ({ ...prev, is_mandatory: e.target.checked }))
              }
              size="small"
            />
            <Typography variant="caption" color="text.secondary" display="block">
              When required and overdue, remains at top of the agenda until completed.
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? <CircularProgress size={20} /> : 'Save Schedule'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ScheduleConfigDialog
